import React, { useState } from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  Divider,
  Grid,
  Banner,
  Button,
  Stack,
  ButtonGroup,
  Input,
  Loader,
  Text,
  TextLink,
} from '@chaser/components';

import CopyButton from '../../../../components/CopyButton';
import LabeledFieldWithError from '../../../../components/LabeledFieldWithError/index.jsx';
import usePush from '../../../../hooks/usePush';
import ConnectService from '../../../../services/connect-service';
import { pendo } from '../../../../util/logging';
import EntitySelectionModal from './EntitySelectionModal';
import SageIntacctLogo from '@/assets/logos/sageintacct.png';

const SageIntacctModal = ({ reconnect, companyId, userId, open, close, onClosed }) => {
  const { execute: executeGetEntities, error } = usePush(ConnectService.getEntitiesSageIntacct, {
    errorToast: 'Failed to connect',
  });

  const [entities, setEntities] = useState(null);
  const [company, setCompany] = useState(null);
  const [credentials, setCredentials] = useState(null);

  const form = useFormik({
    initialValues: {
      companyId: companyId || '',
      userId: userId || '',
      userPassword: '',
    },
    validateOnMount: true,
    validate: ({ userId, companyId, userPassword }) => {
      const errors = {};

      if (!companyId) errors.companyId = 'Please enter a company id.';
      if (!userId) errors.userId = 'Please enter a user id.';
      if (!userPassword) errors.userPassword = 'Please enter a user password.';

      return errors;
    },
    validationSchema: Yup.object().shape({
      companyId: Yup.string().required('You must enter Company ID'),
      userId: Yup.string().required('You must enter User ID'),
      userPassword: Yup.string().required('You must enter User Password'),
    }),
    onSubmit: async ({ userId, companyId, userPassword }, { setSubmitting }) => {
      const [err, data] = await executeGetEntities({ userId, companyId, userPassword });
      setSubmitting(false);
      if (err) return;

      const { entities, company } = data;

      setCredentials({ userId, companyId, userPassword });
      setEntities(entities);
      setCompany(company);

      pendo.track('integration_modal_submitted', {
        integration: 'sageintacct',
        isReconnect: !!reconnect,
      });
    },
  });

  const handleBack = () => {
    setEntities(null);
    setCredentials(null);
  };

  if (credentials && entities) {
    return (
      <EntitySelectionModal
        reconnect={reconnect}
        entities={entities}
        company={company}
        credentials={credentials}
        open={open}
        close={close}
        onClosed={onClosed}
        onBack={handleBack}
      />
    );
  }

  const modalTitle = `${reconnect ? 'Reconnect' : 'Connect'} Organisation`;
  const buttonText = reconnect ? 'Reconnect to Sage Intacct' : 'Continue with Sage Intacct';

  const INTACCT_DOCS_BASE_URL = 'https://www.intacct.com/ia/docs/en_US/help_action';
  const INTACCT_DOCS_WEB_SERVICES_AUTHORIZATIONS =
    'Company/Company_setup/Company_Information/Security/company-web-services-authorizations.htm';
  const INTACCT_DOCS_WEB_SERVICES_USERS =
    'Administration/Users/web-services-only-users.htm?cshid=Web_services_users';
  const HELP_ARTICLE_INTACCT_SETUP = 'https://help.chaserhq.com/use-chaser-with-sage-intacct';
  const CHASER_SENDER_ID = 'chaserMPP';

  return (
    <Modal open={open} toggle={close} onClosed={onClosed}>
      <FormikProvider value={form}>
        <Form>
          <Modal.Body>
            <Stack>
              <img
                src={SageIntacctLogo}
                alt="Sage Intacct"
                style={{ display: 'block', maxHeight: 40, margin: '0 auto' }}
              />
              <Divider />

              <h1 style={{ fontSize: 18, textAlign: 'center', fontWeight: 'bold' }}>
                {modalTitle}
              </h1>

              <Banner intent="info" showCloseButton={false}>
                <TextLink
                  as="a"
                  trim={false}
                  href={HELP_ARTICLE_INTACCT_SETUP}
                  target="_blank"
                  monochrome
                >
                  Follow the link
                </TextLink>
                &nbsp;for detailed instructions on setting up and connecting your Sage Intacct
                organisation.
              </Banner>

              <Divider />

              <Banner intent="info" showCloseButton={false}>
                <ol style={{ marginLeft: 24 }}>
                  <li>
                    Enable the Web Services subscription using &nbsp;
                    <b>Company &gt; Admin &gt; Subscriptions</b>.
                  </li>
                  <li>
                    <TextLink
                      as="a"
                      trim={false}
                      href={`${INTACCT_DOCS_BASE_URL}/${INTACCT_DOCS_WEB_SERVICES_AUTHORIZATIONS}`}
                      target="_blank"
                      monochrome
                    >
                      Authorize
                    </TextLink>
                    &nbsp;our <b>Web Services Sender ID</b> (see below) for the company.
                  </li>
                </ol>
              </Banner>
              <Grid
                columns={2}
                rowGap="medium"
                alignItems="center"
                gridTemplateColumns="180px auto"
                width="100%"
              >
                <Text fontWeight="bold">Web Services Sender ID</Text>
                <Input
                  readonly
                  backgroundColor="Light Neutral 3"
                  iconRight={<CopyButton value={CHASER_SENDER_ID} />}
                  disabled
                  value={CHASER_SENDER_ID}
                  width="100%"
                />
              </Grid>
            </Stack>

            <Divider marginTop={8} marginBottom={24} />

            <Stack>
              <Banner intent="info" showCloseButton={false}>
                <ol style={{ marginLeft: 24 }} start={3}>
                  <li>
                    Create a&nbsp;
                    <TextLink
                      as="a"
                      trim={false}
                      href={`${INTACCT_DOCS_BASE_URL}/${INTACCT_DOCS_WEB_SERVICES_USERS}`}
                      target="_blank"
                      monochrome
                    >
                      Web Services user account
                    </TextLink>
                    &nbsp;with the appropriate permissions. Then, copy the credentials into the form
                    below.
                  </li>
                </ol>
              </Banner>
              <LabeledFieldWithError
                name="companyId"
                as={Input}
                label="Company ID"
                width="100%"
                disabled={!!companyId}
              />
              <LabeledFieldWithError name="userId" as={Input} label="User ID" width="100%" />
              <LabeledFieldWithError
                name="userPassword"
                type="password"
                autocomplete="off"
                as={Input}
                label="User Password"
                width="100%"
              />
              {form.isSubmitting && <Loader />}
              {error && <Text color="Red">{error}</Text>}
            </Stack>
          </Modal.Body>
          <Modal.Footer>
            <ButtonGroup>
              <Button disabled={!form.isValid} loading={form.isSubmitting} type="submit" data-trackingid="integration-sageintacct-connect">
                {buttonText}
              </Button>
            </ButtonGroup>
          </Modal.Footer>
        </Form>
      </FormikProvider>
    </Modal>
  );
};

export default SageIntacctModal;
